import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { getUsers } from '../../../../data/admin'
import { Filters } from '../../../../model/Filters'
import { usePaginationState } from '../../../utils/query'
import { getNextPageParam } from '../../../../misc/query.utilities'
import { useFlatPaginatedData } from '../../../utils/query/useFlatPaginatedData.hook'
import { useCallback, useContext, useMemo } from 'react'
import { UserFriendlyError } from '../../../../model/Error'
import { ConnectedUserContext } from '../../../../misc/auth.utilities'
import { isAdmin, isExtDsm } from '../../../../misc/user.utilities'
import { userKeys } from './users.keys'
import { startingPage } from '../../../../constants'

interface GetInfiniteUsersParams {
  filters?: Filters
  limit?: number
  enabled?: boolean
}

export const useGetInfiniteUsers = ({ filters, limit, enabled = true }: GetInfiniteUsersParams) => {
  const queryKey = useMemo(() => userKeys.list(filters), [filters])
  const { currentUser } = useContext(ConnectedUserContext)

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const { data, ok } = await getUsers(pageParam, filters, signal, limit)
      if (!data || !ok) {
        throw new UserFriendlyError(
          'error',
          `Impossible de récupérer la liste des utilisateurs`,
          `Une erreur technique s'est produite`,
        )
      }
      return data
    },
    initialPageParam: startingPage,
    getNextPageParam,
    enabled: (isAdmin(currentUser) || isExtDsm(currentUser)) && enabled,
  })

  const queryClient = useQueryClient()

  const userList = useFlatPaginatedData(query.data)
  const paginationState = usePaginationState(query.data)

  const cancelPendingQuery = useCallback(() => {
    queryClient.cancelQueries({ queryKey })
  }, [queryClient, queryKey])

  return { query, userList, paginationState, cancelPendingQuery }
}
